import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ATMController.module.css';
import { 
    createAtmTerminal,
    CreateAtmTerminalInput,
    getAtmProviders,
    AtmProvider,
    AtmCredentialInput
} from '../../services/atmTerminals.service';
import { 
    createUsersService, 
    CreateUserInput 
} from '../../services/createUsers.service';
import { authService } from '../../services/auth.service';

interface ShopFormData {
    username: string;
    name: string;
    password: string;
    phone: string;
}

interface AtmFormData {
    pinCode: number;
    phoneForOtp: string;
    port: string;
    recyclerLimitBalance: string;
    address: string;
    remoteId: string;
}

const initialShopFormState: ShopFormData = {
    username: '',
    name: '',
    password: '',
    phone: '',
};

const initialAtmFormState: AtmFormData = {
    pinCode: 0,
    phoneForOtp: '',
    port: '',
    recyclerLimitBalance: '0.00',
    address: '',
    remoteId: '',
};

const initialCredential: AtmCredentialInput = {
    atmProviderId: 0,
    agentName: '',
    agentPassword: '',
    agentKey: '',
};

const ATMCreate = () => {
    const navigate = useNavigate();
    const [shopData, setShopData] = useState<ShopFormData>(initialShopFormState);
    const [atmData, setAtmData] = useState<AtmFormData>(initialAtmFormState);
    const [credentials, setCredentials] = useState<AtmCredentialInput[]>([]);
    const [providers, setProviders] = useState<AtmProvider[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const currentUser = authService.getStoredUser();

    useEffect(() => {
        getAtmProviders()
            .then(setProviders)
            .catch(console.error);
    }, []);

    const handleShopInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setShopData((prev: ShopFormData) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAtmInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        setAtmData((prev: AtmFormData) => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : value,
        }));
    };

    const handleAddCredential = () => {
        setCredentials(prev => [...prev, { ...initialCredential }]);
    };

    const handleRemoveCredential = (index: number) => {
        setCredentials(prev => prev.filter((_, i) => i !== index));
    };

    const handleCredentialChange = (index: number, field: keyof AtmCredentialInput, value: string | number) => {
        setCredentials(prev => prev.map((cred, i) => 
            i === index ? { ...cred, [field]: field === 'atmProviderId' ? Number(value) : value } : cred
        ));
    };

    const isGameVaultProvider = (atmProviderId: number) => {
        const provider = providers.find(p => p.id === atmProviderId);
        return provider?.name?.toUpperCase() === 'GAMEVAULT';
    };

    const handleCancel = () => {
        navigate('/atm-controller');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setSubmitting(true);

        try {
            // Step 1: Create Shop (user)
            const shopUserData: CreateUserInput = {
                username: shopData.username,
                name: shopData.name,
                password: shopData.password,
                role: 'SHOP',
                phone: shopData.phone || undefined,
                host: currentUser?.environment?.host || 'flow888.app',
                parentId: currentUser?.id,
            };

            const createdUser = await createUsersService.createUser(shopUserData);
            const userId = createdUser.id;

            // Step 2: Create ATM Terminal with the new user ID
            const atmInput: CreateAtmTerminalInput = {
                pinCode: atmData.pinCode,
                phoneForOtp: atmData.phoneForOtp,
                port: atmData.port,
                recyclerLimitBalance: atmData.recyclerLimitBalance,
                address: atmData.address,
                remoteId: atmData.remoteId,
                userId: userId,
                balanceRecycler: null,
                balanceCashbox: null,
                credentials: credentials.length > 0 ? credentials : undefined,
            };

            await createAtmTerminal(atmInput);
            setSuccess('Shop and ATM Terminal created successfully!');
            setTimeout(() => {
                navigate('/atm-controller');
            }, 1500);
        } catch (err: any) {
            const errorMessage = err?.graphQLErrors?.[0]?.message || err?.message || 'Failed to create ATM terminal';
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className={styles.main}>
            <div className={styles.headerRow}>
                <h1 className={styles.title}>Create ATM Terminal</h1>
            </div>

            <div className={styles.createFormContainer}>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {error && <div className={styles.error}>{error}</div>}
                    {success && <div className={styles.success}>{success}</div>}

                    {/* Shop Creation Section */}
                    <div className={styles.sectionTitle}>Shop Details</div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Username *</label>
                        <input
                            type="text"
                            name="username"
                            value={shopData.username}
                            onChange={handleShopInputChange}
                            className={styles.formInput}
                            placeholder="Shop username"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Name *</label>
                        <input
                            type="text"
                            name="name"
                            value={shopData.name}
                            onChange={handleShopInputChange}
                            className={styles.formInput}
                            placeholder="Shop name"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Password *</label>
                        <input
                            type="password"
                            name="password"
                            value={shopData.password}
                            onChange={handleShopInputChange}
                            className={styles.formInput}
                            placeholder="Shop password"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Phone</label>
                        <input
                            type="text"
                            name="phone"
                            value={shopData.phone}
                            onChange={handleShopInputChange}
                            className={styles.formInput}
                            placeholder="Phone number"
                        />
                    </div>

                    {/* ATM Terminal Section */}
                    <div className={styles.sectionTitle}>ATM Terminal Details</div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Remote ID *</label>
                        <input
                            type="text"
                            name="remoteId"
                            value={atmData.remoteId}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., MY-ATM-01"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Address *</label>
                        <input
                            type="text"
                            name="address"
                            value={atmData.address}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="ATM location address"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>PIN Code *</label>
                        <input
                            type="number"
                            name="pinCode"
                            value={atmData.pinCode || ''}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., 1234"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Phone for OTP *</label>
                        <input
                            type="text"
                            name="phoneForOtp"
                            value={atmData.phoneForOtp}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., +123456789"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Port *</label>
                        <input
                            type="text"
                            name="port"
                            value={atmData.port}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., COM4"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Recycler Limit Balance *</label>
                        <input
                            type="text"
                            name="recyclerLimitBalance"
                            value={atmData.recyclerLimitBalance}
                            onChange={handleAtmInputChange}
                            className={styles.formInput}
                            placeholder="e.g., 200.00"
                            required
                        />
                    </div>

                    {/* Credentials Section */}
                    <div className={styles.sectionTitle}>Provider Credentials</div>

                    {credentials.map((cred, index) => (
                        <div key={index} className={styles.credentialRow}>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>ATM Provider *</label>
                                <select
                                    value={cred.atmProviderId || ''}
                                    onChange={(e) => handleCredentialChange(index, 'atmProviderId', e.target.value)}
                                    className={styles.formInput}
                                    required
                                >
                                    <option value="">Select Provider</option>
                                    {providers.filter(p => p.isActive).map((provider) => (
                                        <option key={provider.id} value={provider.id}>
                                            {provider.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Agent Name *</label>
                                <input
                                    type="text"
                                    value={cred.agentName}
                                    onChange={(e) => handleCredentialChange(index, 'agentName', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Agent name"
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Password *</label>
                                <input
                                    type="password"
                                    value={cred.agentPassword}
                                    onChange={(e) => handleCredentialChange(index, 'agentPassword', e.target.value)}
                                    className={styles.formInput}
                                    placeholder="Password"
                                    required
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Agent Key {isGameVaultProvider(cred.atmProviderId) ? '*' : ''}
                                </label>
                                <input
                                    type="text"
                                    value={cred.agentKey || ''}
                                    onChange={(e) => handleCredentialChange(index, 'agentKey', e.target.value)}
                                    className={styles.formInput}
                                    placeholder={isGameVaultProvider(cred.atmProviderId) ? 'Agent key (required)' : 'Agent key (optional)'}
                                    required={isGameVaultProvider(cred.atmProviderId)}
                                />
                            </div>
                            <button
                                type="button"
                                className={styles.removeCredentialBtn}
                                onClick={() => handleRemoveCredential(index)}
                            >
                                ✕
                            </button>
                        </div>
                    ))}

                    <button
                        type="button"
                        className={styles.addCredentialBtn}
                        onClick={handleAddCredential}
                    >
                        + Add Provider Credential
                    </button>

                    <div className={styles.formActions}>
                        <button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={handleCancel}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.submitBtn}
                            disabled={submitting}
                        >
                            {submitting ? 'Creating...' : 'Create Shop & ATM'}
                        </button>
                    </div>
                </form>
            </div>
        </main>
    );
};

export default ATMCreate;
